import Input from 'components/Basic/Input';
import {
	SetbacksContainer,
	SetbackItem, SetbackLabel,
	SetbackInput, SetbackUnit,
	Label, SetbackError,
	SetbackInputWithLabel,
	DivGrid, DivError
} from './styles';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { DesignSettings } from 'store/slices/DesignSettingsSlice';
import { useState } from 'react';
import { debounce } from '../../../util';

const MIN_SETBACK_LIMIT = 0, MAX_SETBACK_LIMIT = 100; //in inches;

const ERROR_MSG = {
	'required': 'Setback value is required.',
	'min': `Setback value cannot be less than ${MIN_SETBACK_LIMIT} inches.`,
	'max': `Setback value must not exceed ${MAX_SETBACK_LIMIT} inches.`,
};

const GENERIC_SETBACK_ERROR =
	`Please enter valid setback values between ${MIN_SETBACK_LIMIT} and ${MAX_SETBACK_LIMIT} inches.`;

type SetbackField = {
	label: string,
	key: keyof DesignSettings['setbacks']
}

type Props = {
	disableSetbackInput: boolean;
	onSetbackChnage: (setback: Partial<DesignSettings['setbacks']>, isValid: boolean) => void;
}

type SetbackErrors = Partial<Record<keyof DesignSettings['setbacks'], string>>;

export default function Setback({ onSetbackChnage, disableSetbackInput }: Props) {

	const { setbacks, useEdgeTypeBasedSetbacks} = useSelector((state: RootState) => state.designSettigs.data);	
	const setbacksFields = useEdgeTypeBasedSetbacks ? EDGE_BASED_SETBACKS : SETBACKS;

	const [setbackError, setSetbackError] = useState<SetbackErrors>();

	function handleSetbackFieldChange(
		setback: Partial<DesignSettings['setbacks']>,
		setbackFieldErr: SetbackErrors
	) {
		const updatedErrors = { ...setbackError };

		Object.entries(setbackFieldErr).forEach(([key, err]) => {
			if (err) updatedErrors[key as keyof DesignSettings['setbacks']] = err;
			else delete updatedErrors[key as keyof DesignSettings['setbacks']];
		});

		const hasError = Object.values(updatedErrors).length > 0;

		setSetbackError(updatedErrors);
		onSetbackChnage(setback, !hasError);
	}

	return (
		<SetbacksContainer>
			<Label>Setbacks</Label>
			<DivGrid>
				{setbacksFields.map((field) => (
					<SetbackInputField
						key={field.key}
						id={field.key}
						label={field.label}
						value={setbacks[field.key]}
						onChange={handleSetbackFieldChange}
						isValid={!setbackError?.[field.key]}
						disabled={disableSetbackInput}
					/>
				))}
			</DivGrid>
			<DivError>
				{Object.values(setbackError || {}).some(Boolean) && <SetbackError>{GENERIC_SETBACK_ERROR}</SetbackError>}
			</DivError>
		</SetbacksContainer>
	);
}

type SetbackInoutProps = {
	onChange: (
		setback: Partial<DesignSettings['setbacks']>,
		setbackFieldErr: SetbackErrors
	) => void;
	label: string;
	value: number;
	id: keyof DesignSettings['setbacks'];
	isValid: boolean;
	disabled: boolean;
}

function SetbackInputField(props: SetbackInoutProps) {

	const { label, id, value, isValid, onChange, disabled } = props;
	const [localSetbackVal,setLocalSetback] = useState<string>(`${value}`);
	
	const blockPropagation = (e: React.KeyboardEvent<HTMLInputElement>) => {
		e.stopPropagation();
	};

	function handleSetbackInputChange(evt: React.ChangeEvent<HTMLInputElement>) {
		setLocalSetback(evt.target.value);
		const val = evt.target.value ? + evt.target.value : null;
		const fieldID = evt.target.id as keyof DesignSettings['setbacks'];
		const setbackFieldErr: SetbackErrors = { [fieldID]: '' };

		if (val === null) setbackFieldErr[fieldID] = ERROR_MSG['required'];
		else if (val < MIN_SETBACK_LIMIT) setbackFieldErr[fieldID] = ERROR_MSG['min'];
		else if (val > MAX_SETBACK_LIMIT) setbackFieldErr[fieldID] = ERROR_MSG['max'];

		onChange({ [fieldID]: (val) }, setbackFieldErr);
	}

	return (
		<SetbackItem>
			<SetbackInputWithLabel>
				<SetbackLabel>{label}</SetbackLabel>
				<SetbackInput>
					<Input
						id={id}
						type="number"
						step="any"
						className={!isValid ? 'input-error' : ''}
						value={localSetbackVal}
						onChange={handleSetbackInputChange}
						onKeyUp={blockPropagation}
						min={MIN_SETBACK_LIMIT}
						max={MAX_SETBACK_LIMIT}
						disabled={disabled}
					/>
					<SetbackUnit>in</SetbackUnit>
				</SetbackInput>
			</SetbackInputWithLabel>
		</SetbackItem>
	);
}

const EDGE_BASED_SETBACKS: Array<SetbackField> = [
	{ label: 'Gutter', key: 'gutters' },
	{ label: 'Eave', key: 'eaves' },
	{ label: 'Rake', key: 'rakes' },
	{ label: 'Valley', key: 'valleys' },
	{ label: 'Hip', key: 'hips' },
	{ label: 'Ridge', key: 'ridges' },
	{ label: 'Obstructions', key: 'obstructions' },
];

const SETBACKS: Array<SetbackField> = [
	{ label: 'Gutter', key: 'gutters' },
	{ label: 'Other', key: 'nonGutter' },
	{ label: 'Obstructions', key: 'obstructions' },
];
